import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Download, ExternalLink, Trash2, Search, X, FolderOpen, Image as ImageIcon } from "lucide-react";
import api, { errMsg } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";

const IMAGE_TYPES = ["PNG", "JPG", "JPEG"];

function POThumb({ row, onOpen }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const isImage = IMAGE_TYPES.includes((row.fileType || "").toUpperCase());

  useEffect(() => {
    if (!isImage) return undefined;
    let objectUrl = null;
    let cancelled = false;
    api.get("/po/download", { params: { path: row.storagePath, mode: "view" }, responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setUrl(objectUrl);
      })
      .catch(() => setFailed(true));
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [isImage, row.storagePath]);

  return (
    <button
      type="button"
      onClick={onOpen}
      title="Preview PO"
      className="h-12 w-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden hover:border-amber-400 transition-colors"
      data-testid={`po-thumb-${row.oaNumber}`}
    >
      {isImage && url && !failed ? (
        <img src={url} alt={row.filename} className="h-full w-full object-cover" onError={() => setFailed(true)} />
      ) : isImage ? (
        <ImageIcon className="h-5 w-5 text-slate-400" />
      ) : (
        <FileText className="h-5 w-5 text-slate-400" />
      )}
    </button>
  );
}

function POViewerModal({ row, canDownload, onClose, onDownload }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState("");
  const ft = (row.fileType || "").toUpperCase();
  const isPdf = ft === "PDF";
  const isImage = ["PNG", "JPG", "JPEG", "WEBP", "GIF"].includes(ft);
  const previewable = isPdf || isImage;

  useEffect(() => {
    if (!previewable) return undefined;
    let objectUrl = null;
    let cancelled = false;
    api.get("/po/download", { params: { path: row.storagePath, mode: "view" }, responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setUrl(objectUrl);
      })
      .catch((e) => setErr(errMsg(e)));
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [previewable, row.storagePath]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="po-viewer-modal" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-200">
          <div className="min-w-0">
            <div className="font-head font-bold text-slate-900 truncate" data-testid="po-viewer-title">{row.filename}</div>
            <div className="text-xs text-slate-400">
              {row.poNumber ? `PO ${row.poNumber} · ` : ""}{row.customerName} · {row.oaNumber} · {row.fileType}{row.fileSize ? ` · ${Math.round(row.fileSize / 1024)} KB` : ""}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canDownload && <button className="btn-outline btn-sm" onClick={() => onDownload(row)} data-testid="po-viewer-download-btn"><Download className="h-4 w-4" /> Download Original PO</button>}
            <button className="p-1.5 rounded hover:bg-slate-100" onClick={onClose} data-testid="po-viewer-close-btn"><X className="h-5 w-5 text-slate-600" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-slate-50 min-h-[300px] flex items-center justify-center" data-testid="po-viewer-body">
          {previewable && url && isPdf && <iframe src={url} title={row.filename} className="w-full h-[70vh] rounded border border-slate-200 bg-white" data-testid="po-viewer-frame" />}
          {previewable && url && isImage && <img src={url} alt={row.filename} className="max-h-[70vh] max-w-full object-contain rounded border border-slate-200 bg-white" data-testid="po-viewer-image" />}
          {previewable && !url && !err && <div className="text-sm text-slate-400">Loading original PO…</div>}
          {err && <div className="text-sm text-red-600" data-testid="po-viewer-error">{err}</div>}
          {!previewable && (
            <div className="text-center space-y-3 py-10">
              <FileText className="h-12 w-12 text-slate-300 mx-auto" />
              <div className="text-sm text-slate-500" data-testid="po-viewer-no-preview">Preview not available for this file type.</div>
              {canDownload && <button className="btn-primary btn-sm" onClick={() => onDownload(row)} data-testid="po-viewer-download-original-btn"><Download className="h-4 w-4" /> Download Original PO</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ViewAllPO() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const perms = user.permissions || [];
  const canView = isAdmin || perms.includes("view_po");
  const canDownload = isAdmin || perms.includes("download_po");
  const canDelete = isAdmin || perms.includes("delete_po");
  const [rows, setRows] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [filters, setFilters] = useState({ search: "", customer: "", oaNumber: "", dateFrom: "", dateTo: "", uploadedBy: "", fileType: "" });

  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/po", { params });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [filters]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const set = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const clear = () => setFilters({ search: "", customer: "", oaNumber: "", dateFrom: "", dateTo: "", uploadedBy: "", fileType: "" });

  const openPO = async (r, download) => {
    try {
      const { data } = await api.get("/po/download", { params: { path: r.storagePath, mode: download ? "download" : "view" }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (download) {
        const a = document.createElement("a"); a.href = url; a.download = r.filename || "PO"; document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, "_blank");
      }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete PO for ${r.oaNumber}?`)) return;
    try { await api.delete(`/po/${r.oaId}`); toast.success("PO deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="View All PO" subtitle={`${rows.length} uploaded purchase order(s)`} />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="card p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div className="relative col-span-2 md:col-span-1">
            <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
            <input className="input pl-9" placeholder="Search PO / Cust / OA" value={filters.search} onChange={(e) => set("search", e.target.value)} data-testid="po-search" />
          </div>
          <input className="input" placeholder="Customer" value={filters.customer} onChange={(e) => set("customer", e.target.value)} data-testid="po-filter-customer" />
          <input className="input" placeholder="OA Number" value={filters.oaNumber} onChange={(e) => set("oaNumber", e.target.value)} data-testid="po-filter-oa" />
          <input type="date" className="input" value={filters.dateFrom} onChange={(e) => set("dateFrom", e.target.value)} data-testid="po-filter-from" />
          <input type="date" className="input" value={filters.dateTo} onChange={(e) => set("dateTo", e.target.value)} data-testid="po-filter-to" />
          <select className="input" value={filters.fileType} onChange={(e) => set("fileType", e.target.value)} data-testid="po-filter-type">
            <option value="">All Types</option>
            {["pdf", "xls", "xlsx", "csv", "png", "jpg", "jpeg"].map((x) => <option key={x} value={x}>{x.toUpperCase()}</option>)}
          </select>
          <button className="btn-ghost btn-sm" onClick={clear}><X className="h-4 w-4" /> Clear</button>
        </div>

        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                <th className="th">Preview</th><th className="th">PO No.</th><th className="th">PO Date</th><th className="th">Customer</th><th className="th">OA No.</th>
                <th className="th">File</th><th className="th">Type</th><th className="th">Uploaded By</th><th className="th">Upload Date</th><th className="th">Status</th><th className="th">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={11}><div className="flex items-center gap-2 py-6 justify-center"><FolderOpen className="h-5 w-5" /> No purchase orders uploaded yet.</div></td></tr>}
                {rows.map((r) => (
                  <tr key={r.oaId} className="hover:bg-slate-50" data-testid={`po-row-${r.oaNumber}`}>
                    <td className="td"><POThumb row={r} onOpen={() => openPO(r, false)} /></td>
                    <td className="td font-semibold text-slate-900">{r.poNumber || "—"}</td>
                    <td className="td">{r.poDate || "—"}</td>
                    <td className="td">{r.customerName}</td>
                    <td className="td font-mono">{r.oaNumber}</td>
                    <td className="td max-w-[180px] truncate"><span className="flex items-center gap-1.5"><FileText className="h-4 w-4 text-slate-400 shrink-0" />{r.filename}</span></td>
                    <td className="td"><span className="badge bg-slate-100 text-slate-700 border border-slate-300">{r.fileType}</span></td>
                    <td className="td text-xs">{r.uploadedBy || "—"}</td>
                    <td className="td text-xs text-slate-400">{(r.uploadDate || "").slice(0, 10)}</td>
                    <td className="td"><StatusBadge status={r.status} /></td>
                    <td className="td"><div className="flex gap-1">
                      {canView && <button title="View PO" className="p-1.5 rounded hover:bg-slate-100" onClick={() => setViewer(r)} data-testid={`po-open-${r.oaNumber}`}><ExternalLink className="h-4 w-4 text-slate-600" /></button>}
                      {canDownload && <button title="Download" className="p-1.5 rounded hover:bg-slate-100" onClick={() => openPO(r, true)} data-testid={`po-download-${r.oaNumber}`}><Download className="h-4 w-4 text-slate-600" /></button>}
                      <button title="View OA" className="p-1.5 rounded hover:bg-slate-100" onClick={() => navigate(`/oa/${r.oaId}/preview`)}><FileText className="h-4 w-4 text-slate-600" /></button>
                      {canDelete && <button title="Delete" className="p-1.5 rounded hover:bg-red-50" onClick={() => del(r)} data-testid={`po-delete-${r.oaNumber}`}><Trash2 className="h-4 w-4 text-red-600" /></button>}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {viewer && <POViewerModal row={viewer} canDownload={canDownload} onClose={() => setViewer(null)} onDownload={(r) => openPO(r, true)} />}
    </div>
  );
}
